import { audioEngine } from './AudioEngine';

/**
 * sfx.ts — every one-shot sound in the game, synthesized at call time:
 * ball thumps, power-ups, countdown beeps, UI ticks, fanfares. Each helper
 * is a no-op until the engine is unlocked.
 */

function ctxBus(): [AudioContext, GainNode] | null {
  const ctx = audioEngine.context;
  const bus = audioEngine.sfxBus;
  if (!ctx || !bus) return null;
  return [ctx, bus];
}

/** A quick enveloped oscillator — the workhorse. */
function blip(
  type: OscillatorType,
  f0: number,
  f1: number,
  dur: number,
  gain: number,
  when = 0,
): void {
  const cb = ctxBus();
  if (!cb) return;
  const [ctx, bus] = cb;
  const t = ctx.currentTime + when;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(f0, t);
  osc.frequency.exponentialRampToValueAtTime(Math.max(1, f1), t + dur);
  g.gain.setValueAtTime(gain, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + dur);
  osc.connect(g).connect(bus);
  osc.start(t);
  osc.stop(t + dur + 0.02);
}

/** A burst of filtered noise (impacts, scrapes, dust). */
function noiseBurst(dur: number, gain: number, filterHz: number, type: BiquadFilterType, when = 0): void {
  const cb = ctxBus();
  if (!cb) return;
  const [ctx, bus] = cb;
  const t = ctx.currentTime + when;
  const len = Math.max(1, Math.floor(ctx.sampleRate * dur));
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const filter = ctx.createBiquadFilter();
  filter.type = type;
  filter.frequency.value = filterHz;
  const g = ctx.createGain();
  g.gain.setValueAtTime(gain, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + dur);
  src.connect(filter).connect(g).connect(bus);
  src.start(t);
}

/** The ball thump — weight scales with impact speed (0..1). */
export function sfxBallHit(intensity: number): void {
  const k = Math.min(1, intensity);
  blip('sine', 160 + 60 * k, 55, 0.16 + 0.1 * k, 0.5 * k + 0.12);
  noiseBurst(0.07, 0.16 * k, 1200, 'lowpass');
}

export function sfxWallScrapeTick(speed01: number): void {
  noiseBurst(0.09, 0.1 + 0.12 * speed01, 2400, 'bandpass');
}

export function sfxBoost(): void {
  blip('sawtooth', 180, 720, 0.45, 0.22);
  noiseBurst(0.35, 0.12, 3200, 'highpass');
}

export function sfxMagnet(): void {
  blip('square', 220, 880, 0.18, 0.16);
  blip('square', 330, 1320, 0.22, 0.12, 0.06);
}

export function sfxDozerHit(): void {
  blip('sine', 120, 38, 0.4, 0.6);
  noiseBurst(0.28, 0.35, 700, 'lowpass');
  blip('sawtooth', 90, 30, 0.3, 0.25, 0.02);
}

/** Driving through an item box (M12) — a bright grab. */
export function sfxCollect(): void {
  blip('triangle', 520, 1040, 0.12, 0.2);
  blip('triangle', 780, 1560, 0.14, 0.14, 0.05);
}

/** The HUD roulette (M12): decelerating ticks over `duration`, Mario-Kart style. */
export function sfxRouletteSpin(duration: number): void {
  const ticks = 8;
  for (let i = 0; i < ticks; i++) {
    // Quadratic spacing → ticks start fast and slow toward the reveal.
    const when = duration * Math.pow(i / ticks, 1.5);
    blip('square', 980, 980, 0.03, 0.07, when);
  }
}

/** The roulette lands — your item is ready. */
export function sfxItemLand(): void {
  blip('triangle', 660, 660, 0.09, 0.18);
  blip('triangle', 990, 990, 0.18, 0.16, 0.07);
}

/** Item denied (too far from your ball) — a flat double-buzz. */
export function sfxDeny(): void {
  blip('square', 160, 140, 0.09, 0.16);
  blip('square', 120, 100, 0.12, 0.16, 0.1);
}

/** Shield up (M12) — a rising shimmer. */
export function sfxShield(): void {
  blip('sine', 440, 1320, 0.3, 0.16);
  blip('sine', 660, 1980, 0.34, 0.1, 0.05);
}

/** A bulldozer ram bouncing off a shield — a hard clink, no damage. */
export function sfxShieldBlock(): void {
  blip('square', 1400, 700, 0.1, 0.2);
  noiseBurst(0.06, 0.12, 4000, 'highpass');
}

/** KICK (M12) — a punchy soccer strike. */
export function sfxKick(): void {
  blip('sine', 210, 70, 0.14, 0.5);
  noiseBurst(0.09, 0.25, 1800, 'bandpass');
}

/** SLIDE TACKLE launch (M14) — the ghost tears off up the course. */
export function sfxTackleLaunch(): void {
  blip('sawtooth', 140, 880, 0.5, 0.2);
  noiseBurst(0.45, 0.14, 2400, 'highpass');
}

/** The slide tackle connecting (M58 production pass) — a tyre squeal over the
 *  turf skid, a DOUBLE body check with sub-bass weight, and a short crowd
 *  "ooooh" swelling up after the hit. A tackle should sound like an event. */
export function sfxTackleHit(): void {
  noiseBurst(0.38, 0.35, 1400, 'bandpass'); // the skid
  blip('sawtooth', 1250, 720, 0.4, 0.1, 0.02); // tyre squeal over the skid
  blip('sine', 150, 45, 0.32, 0.55, 0.04); // the body thud
  blip('sine', 82, 34, 0.4, 0.4, 0.06); // sub layer under the thud — the WEIGHT
  blip('square', 700, 180, 0.12, 0.18, 0.02);
  noiseBurst(0.5, 0.07, 1100, 'bandpass', 0.14); // the terraces react…
  noiseBurst(0.4, 0.05, 1900, 'bandpass', 0.24); // …swelling brighter
}

/** A car spinning out (M58) — a pitch-falling tyre squeal (two detuned voices
 *  so it warbles like real rubber) over a skidding-noise bed. `hard` = a full
 *  spin (tackle/dozer/weight-clash swat); soft = a stagger/offside wobble. */
export function sfxSpinOut(hard = true): void {
  const k = hard ? 1 : 0.6;
  blip('sawtooth', 1350, 820, 0.5 * k, 0.09 * k);
  blip('sawtooth', 1180, 700, 0.44 * k, 0.08 * k, 0.03);
  noiseBurst(0.5 * k, 0.16 * k, 2600, 'bandpass');
  noiseBurst(0.35 * k, 0.1 * k, 1200, 'bandpass', 0.08);
}

/** FREE KICK WALL planted (M46) — three quick heavy stomps as the wall sets. */
export function sfxWallPlant(): void {
  blip('sine', 130, 60, 0.12, 0.4);
  noiseBurst(0.06, 0.14, 800, 'lowpass');
  blip('sine', 140, 65, 0.12, 0.38, 0.09);
  noiseBurst(0.06, 0.14, 800, 'lowpass', 0.09);
  blip('sine', 150, 70, 0.14, 0.42, 0.18);
  noiseBurst(0.08, 0.18, 900, 'lowpass', 0.18);
}

/** A free-kick wall shattering (M46, a Pibe ran through it) — a dry crunch. */
export function sfxWallBreak(): void {
  noiseBurst(0.22, 0.3, 2000, 'bandpass');
  blip('sine', 160, 50, 0.2, 0.35, 0.02);
  noiseBurst(0.14, 0.16, 4200, 'highpass', 0.05);
}

/** OFFSIDE TRAP (M46) — the referee's sharp double whistle. */
export function sfxOffside(): void {
  blip('square', 2250, 2050, 0.13, 0.16);
  noiseBurst(0.1, 0.05, 2200, 'bandpass');
  blip('square', 2250, 1950, 0.24, 0.18, 0.18);
  noiseBurst(0.2, 0.06, 2200, 'bandpass', 0.18);
}

/** Gap between the staggered plucks inside one guitar strum (low string first). */
const STRUM_STAGGER = 0.012;

/**
 * One fast nylon-guitar strum (private helper for sfxPibe): the chord's five
 * "strings" are plucked lowest-first, 12 ms apart — too fast to hear as
 * separate notes, so the ear reads it as a single rasgueado brush across a
 * guitar. Each pluck starts a hair sharp and settles onto its pitch (that
 * tiny bend is what makes it sound plucked instead of beeped), and a short
 * high-noise tick on top is the fingernail catching the strings.
 */
function strum(freqs: number[], gain: number, when: number): void {
  freqs.forEach((f, i) => {
    blip('triangle', f * 1.02, f, 0.24, gain, when + i * STRUM_STAGGER);
  });
  noiseBurst(0.03, 0.05, 3500, 'highpass', when); // the fingernail scrape
}

/**
 * PIBE DE ORO ignites (M46) — an Argentine street-football fanfare, ~1.4 s.
 * Musical intent: three quick strummed guitar chords walk the classic
 * tango-milonga cadence Am → E7 → Am (minor and dramatic, not a cheery
 * major arpeggio), a bright trumpet calls a short phrase over the top
 * (the F-falling-to-E "sigh" is the signature tango/phrygian move), and a
 * stadium crowd swells up underneath — the "goooool" energy without words.
 */
export function sfxPibe(): void {
  // The guitar. Chord voicings low→high, like open guitar strings:
  const AM = [220.0, 329.63, 440.0, 523.25, 659.26]; // A minor — home, moody
  const E7 = [164.81, 415.3, 493.88, 587.33, 659.26]; // E7 — the tension pull
  strum(AM, 0.12, 0.0);
  strum(E7, 0.12, 0.17);
  strum(AM, 0.15, 0.35); // the resolving strum lands hardest

  // The trumpet, over the last chord: a pickup E, then F sighing down to E
  // (the tango lament), then a held high A that droops off as it fades —
  // like a commentator's cry losing breath.
  blip('sawtooth', 659.26, 659.26, 0.13, 0.14, 0.4); // E5 pickup
  blip('sawtooth', 698.46, 652, 0.16, 0.15, 0.54); // F5 falling onto E5
  blip('sawtooth', 880, 820, 0.42, 0.16, 0.72); // A5 held, bending down

  // The crowd: stacked noise layers, each louder and brighter than the
  // last = a terrace roar rising under the trumpet's held note.
  noiseBurst(0.35, 0.04, 900, 'bandpass', 0.6);
  noiseBurst(0.35, 0.07, 1400, 'bandpass', 0.78);
  noiseBurst(0.4, 0.1, 2000, 'bandpass', 0.95);
  noiseBurst(0.45, 0.13, 2800, 'highpass', 1.05);
}

/** STUDS OUT igniting (M59) — a metallic motor whir as the spikes deploy. */
export function sfxStuds(): void {
  blip('sawtooth', 90, 300, 0.5, 0.13); // rising motor whir
  blip('square', 340, 560, 0.42, 0.08, 0.05); // metallic overtone
  noiseBurst(0.5, 0.1, 3800, 'highpass'); // metal-on-metal hiss
}

/** A rival shredded on the studs (M59) — a sharp clang + metal shred + thud. */
export function sfxStudsHit(): void {
  blip('square', 1500, 520, 0.14, 0.2); // the clang
  noiseBurst(0.2, 0.3, 3000, 'bandpass'); // the shred
  blip('sine', 150, 45, 0.24, 0.42, 0.02); // the body thud under it
}

/** SCORPION KICK (M59) — a rising whoosh as the ball lofts, then the kick thump. */
export function sfxScorpion(): void {
  blip('sine', 200, 940, 0.34, 0.17); // whoosh up (rising pitch)
  noiseBurst(0.3, 0.1, 1900, 'bandpass', 0.02); // air over the flip
  blip('sine', 240, 70, 0.16, 0.5, 0.3); // the boot connecting at the apex
  noiseBurst(0.1, 0.22, 1600, 'bandpass', 0.3);
}

/** SLIPPERY MUD poured (M59) — a wet, gloopy splat spreading across the road. */
export function sfxMud(): void {
  noiseBurst(0.42, 0.3, 700, 'lowpass'); // the wet splat (low, dull)
  blip('sine', 130, 55, 0.3, 0.3, 0.02); // a gloop
  noiseBurst(0.26, 0.14, 1500, 'bandpass', 0.12); // spatter tailing off
}

/** Dropping into a SECRET SHORTCUT (M116) — a swallowed air-rush whoosh under a
 *  bright rising "found it!" chime pair (the discovery is the reward). */
export function sfxShortcut(): void {
  noiseBurst(0.5, 0.24, 900, 'lowpass'); // the swallow — air rushing past the mouth
  blip('sine', 520, 900, 0.2, 0.2, 0.02); // rising find-chime…
  blip('sine', 780, 1400, 0.28, 0.15, 0.12); // …and its sparkle an octave-ish up
}

/** The car transforming into the bulldozer (M12) — heavy mechanical clanks. */
export function sfxDozerTransform(): void {
  blip('square', 90, 55, 0.16, 0.3);
  noiseBurst(0.1, 0.2, 900, 'lowpass');
  blip('square', 130, 80, 0.14, 0.26, 0.12);
  noiseBurst(0.12, 0.22, 1100, 'lowpass', 0.12);
}

export function sfxLand(intensity: number): void {
  const k = Math.min(1, intensity);
  blip('sine', 110, 45, 0.18, 0.35 * k);
  noiseBurst(0.12, 0.2 * k, 900, 'lowpass');
}

/** The ball hitting the finish goal's net (M157): the soft "poof" of the
 *  mesh taking the ball, then the rustle of netting as it ripples out. */
export function sfxNetSwish(intensity: number): void {
  const k = Math.min(1, Math.max(0.25, intensity));
  blip('sine', 120, 52, 0.22, 0.32 * k); // the poof as the net gives
  noiseBurst(0.38, 0.18 * k, 2600, 'bandpass', 0.02); // the ripple…
  noiseBurst(0.3, 0.08 * k, 5200, 'highpass', 0.12); // …and the cord rustle
}

export function sfxCountdown(final: boolean): void {
  blip('square', final ? 880 : 440, final ? 880 : 440, final ? 0.34 : 0.14, 0.22);
}

export function sfxUiTick(): void {
  blip('triangle', 660, 660, 0.05, 0.12);
}

/** The finish fanfare — placement-flavoured arpeggio. */
export function sfxFanfare(place: number): void {
  const win = place === 1;
  const base = win ? 392 : 330; // G4 vs E4
  const steps = win ? [0, 4, 7, 12, 16] : [0, 3, 7, 10];
  steps.forEach((semi, i) => {
    blip('triangle', base * Math.pow(2, semi / 12), base * Math.pow(2, semi / 12), 0.34, 0.2, i * 0.12);
  });
  if (win) noiseBurst(0.5, 0.1, 6000, 'highpass', steps.length * 0.12);
}

export function sfxChampion(): void {
  const base = 523;
  [0, 4, 7, 12, 7, 12, 16, 19].forEach((semi, i) => {
    blip('triangle', base * Math.pow(2, semi / 12), base * Math.pow(2, semi / 12), 0.4, 0.18, i * 0.15);
  });
}

/** The post-login WELCOME BACK bubble: a fly-by whoosh into a short tyre
 *  chirp as it skids to a stop (lighter than a spin-out — it's a greeting). */
export function sfxWelcomeSkid(): void {
  noiseBurst(0.28, 0.08, 3800, 'highpass');
  blip('sawtooth', 1250, 900, 0.22, 0.05, 0.3);
  blip('sawtooth', 1120, 820, 0.2, 0.045, 0.32);
  noiseBurst(0.22, 0.09, 2400, 'bandpass', 0.3);
}
